import { PORTAL_CSRF_TOKEN, PORTAL_EXPIRES, PORTAL_POSTPONED_ACCESS_RIGHTS, USER } from "constants/StorageConstants"
import { storeLocale } from "hooks/useLocale"
import React, { ReactNode, createContext, useContext, useState } from "react"
import * as AuthService from "services/AuthService"
import { onConnectionError } from "services/generic/AxiosInterceptor/onResponseRejected"

import { AuthState, AuthType, AuthenticatedParams } from "./Auth.interface"

interface AuthContextType extends AuthState {
  setUserAuthentication: (params: AuthenticatedParams) => void
  signOut: () => Promise<void>
  isLoading: boolean
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

const getInitialValue = (): AuthState => {
  let initialState: AuthState = {
    type: AuthType.Invalid,
  }

  try {
    const getStoredValue = (key: string) => localStorage.getItem(key) ?? sessionStorage.getItem(key)
    const userString = getStoredValue(USER)
    const userExpiresString = getStoredValue(PORTAL_EXPIRES)

    if (userString && userExpiresString) {
      initialState = {
        type: AuthType.Authenticated,
        user: JSON.parse(userString),
        userExpires: new Date(userExpiresString),
      }
    }
  } catch {
    // Just use invalid state if there are any issues parsing
  }

  return initialState
}

/** Helper to clear both local- and session storage */
const clearStorages = () => {
  const keysToRemove = [USER, PORTAL_CSRF_TOKEN, PORTAL_EXPIRES]

  keysToRemove.forEach((key) => {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
  })

  // Only clear "postponed" access rights from session storage
  sessionStorage.removeItem(PORTAL_POSTPONED_ACCESS_RIGHTS)
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(() => getInitialValue())
  const [isLoading, setIsLoading] = useState(false)

  const setUserAuthentication = ({ user, csrfToken, userExpires, storeCredentials }: AuthenticatedParams) => {
    // Update state
    setAuthState({
      type: AuthType.Authenticated,
      user,
      userExpires,
    })

    // Clear stale data, in case a user logs in during the `7 day`
    // period (but without the `mfaRemember` checked this time)
    clearStorages()

    // Sync locale from the backend (the backend is the source of truth), which
    // also switches the mounted `IntlProvider` over without a page reload
    storeLocale(user.locale)

    // If `mfaRemember` (remember me checkbox) is set we use `localStorage` to keep data
    // even if the user closes the browser
    const storage = storeCredentials ? localStorage : sessionStorage
    storage.setItem(USER, JSON.stringify(user))
    storage.setItem(PORTAL_EXPIRES, userExpires.toISOString())

    if (csrfToken) {
      storage.setItem(PORTAL_CSRF_TOKEN, csrfToken)
    }
  }

  const signOut = async () => {
    setIsLoading(true)

    try {
      await AuthService.logout()
    } catch (error) {
      // Called outside React Query, which handles a request without a response everywhere else (see `api/client.ts`)
      onConnectionError(error)
      throw error
    } finally {
      // Update state
      setAuthState({
        type: AuthType.Invalid,
        user: undefined,
        userExpires: undefined,
      })

      clearStorages()
      setIsLoading(false)

      // TODO: Navigation side effect should be handled by router
      window.location.href = "/auth/login?loggedOut"
    }
  }

  const value: AuthContextType = {
    ...authState,
    setUserAuthentication,
    signOut,
    isLoading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthContext provider")
  }
  return context
}
